/**
 * CloudMaintenanceTable — shared table for Batteries, Communication Lost, and Other tabs
 * on the Cloud 5.0 alerts page.
 *
 * Columns: Detected, Duration, Condition, Device/Serial, Area, Resident
 *
 * No Clear button (unlike the 4.0 MaintenanceTable).
 * Data comes from `getdevicemaintenance` stored procedure, split by condition.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow } from '../../../../libs.jsx';

function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const CloudMaintenanceTable = ({ data, sortColumn, sortOrder, onSort }) => {
    useTick(1000);

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '\u2014';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const getDeviceLink = (row) => {
        const mfg = parseInt(row.manufacture_id, 10);
        if (mfg === 1) return `/profiles/inovonics/${row.device_id}`;
        if (mfg === 4) return `/profiles/palcare/${row.device_id}`;
        return null;
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_datetime_utc')}>Detected{sortIcon('event_datetime_utc')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('condition')}>Condition{sortIcon('condition')}</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('area_name')}>Area{sortIcon('area_name')}</th>
                        <th className="dp-th">Resident</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={6}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No alerts in this category</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row, idx) => {
                        const deviceLink = getDeviceLink(row);
                        return (
                            <tr key={row.device_id || idx} className="dp-tr">
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.event_datetime_utc)}</td>
                                <td className="dp-td"><span className="na-duration">{durationToNow(row.event_datetime_utc)}</span></td>
                                <td className="dp-td">{row.condition || '\u2014'}</td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {deviceLink ? (
                                            <Link to={deviceLink} className="ar-link">{row.device_name || '\u2014'}</Link>
                                        ) : (<span>{row.device_name || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.manufacture_uuid || ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '\u2014'}</Link>
                                    ) : (row.area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.resident_id ? (
                                        <Link to={`/profiles/resident/${row.resident_id}`} className="ar-link">{row.resident_name || '\u2014'}</Link>
                                    ) : (row.resident_name || '\u2014')}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CloudMaintenanceTable;
